<template><markdown-wrapper><h1 id="alert">Alert <a class="header-anchor" href="#alert">&#128279;</a></h1>
<blockquote>
<p>Provide contextual feedback messages for typical user actions with the handful of available and flexible alert messages.</p>
</blockquote>
<p><ins class="adsbygoogle"
     style="display:block; text-align:center;"
     data-ad-layout="in-article"
     data-ad-format="fluid"
     data-ad-client="ca-pub-4714899946256166"
     data-ad-slot="4603582855"></ins></p>
<h2 id="examples">Examples <a class="header-anchor" href="#examples">&#128279;</a></h2>
<p>Wrap any text or HTML in <code v-pre>&lt;alert&gt;</code> and use one of the four alert types (<code v-pre>success</code> / <code v-pre>info</code> / <code v-pre>warning</code> / <code v-pre>danger</code>) for basic alert messages.</p>
<alert-examples ref="alert-examples"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;success&quot;</span>&gt;</span><span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>Well done!<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span> You successfully read this important alert message.<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;info&quot;</span>&gt;</span><span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>Heads up!<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span> This alert needs your attention, but it&#x27;s not super important.<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;warning&quot;</span>&gt;</span><span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>Warning!<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span> Better check yourself, you&#x27;re not looking too good.<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;danger&quot;</span>&gt;</span><span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>Oh snap!<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span> Change this and that and try again.<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
<span class="hljs-comment">&lt;!-- alert-examples.vue --&gt;</span>
</code></pre>
<h2 id="dismissible">Dismissible <a class="header-anchor" href="#dismissible">&#128279;</a></h2>
<p>Use <code v-pre>dismissible</code> to allow user to dismiss alerts.</p>
<alert-dismissible ref="alert-dismissible"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;warning&quot;</span> <span class="hljs-attr">v-if</span>=<span class="hljs-string">&quot;show&quot;</span> <span class="hljs-attr">dismissible</span> @<span class="hljs-attr">dismissed</span>=<span class="hljs-string">&quot;show = false&quot;</span>&gt;</span>
      <span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>Warning!<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span> Better check yourself, you&#x27;re not looking too good.
    <span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;(item, index) in alerts&quot;</span> <span class="hljs-attr">dismissible</span> <span class="hljs-attr">:key</span>=<span class="hljs-string">&quot;item.key&quot;</span> @<span class="hljs-attr">dismissed</span>=<span class="hljs-string">&quot;alerts.splice(index, 1)&quot;</span>&gt;</span>
      <span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>Heads up!<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span> This alert needs your attention, but it&#x27;s not super important.
    <span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">hr</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;addDismissibleAlert()&quot;</span>&gt;</span>Add Dismissible Alert<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">show</span>: <span class="hljs-literal">true</span>,
        <span class="hljs-attr">alerts</span>: []
      }
    },
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">addDismissibleAlert</span> () {
        <span class="hljs-variable language_">this</span>.<span class="hljs-property">alerts</span>.<span class="hljs-title function_">push</span>({<span class="hljs-attr">key</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>().<span class="hljs-title function_">getTime</span>()})
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- alert-dismissible.vue --&gt;</span>
</code></pre>
<h2 id="auto-dismissing">Auto dismissing <a class="header-anchor" href="#auto-dismissing">&#128279;</a></h2>
<p>Use <code v-pre>duration</code> in milliseconds to auto dismiss alert. It can be used together with <code v-pre>dismissible</code>.</p>
<alert-auto-dismissing ref="alert-auto-dismissing"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">v-for</span>=<span class="hljs-string">&quot;(item, index) in alerts&quot;</span> <span class="hljs-attr">:duration</span>=<span class="hljs-string">&quot;duration&quot;</span> <span class="hljs-attr">:key</span>=<span class="hljs-string">&quot;item.key&quot;</span> @<span class="hljs-attr">dismissed</span>=<span class="hljs-string">&quot;alerts.splice(index, 1)&quot;</span>&gt;</span>
      This alert <span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>will dismiss after {{duration}}ms<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span>.
    <span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">hr</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;addAutoDismissAlert()&quot;</span>&gt;</span>Add Auto Dismiss Alert<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">alerts</span>: [],
        <span class="hljs-attr">duration</span>: <span class="hljs-number">2000</span>
      }
    },
    <span class="hljs-attr">methods</span>: {
      <span class="hljs-title function_">addAutoDismissAlert</span> () {
        <span class="hljs-variable language_">this</span>.<span class="hljs-property">alerts</span>.<span class="hljs-title function_">push</span>({<span class="hljs-attr">key</span>: <span class="hljs-keyword">new</span> <span class="hljs-title class_">Date</span>().<span class="hljs-title function_">getTime</span>()})
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- alert-auto-dismissing.vue --&gt;</span>
</code></pre>
<h2 id="use-with-collapse">Use with collapse <a class="header-anchor" href="#use-with-collapse">&#128279;</a></h2>
<alert-with-collapse ref="alert-with-collapse"/>
<pre v-pre><code v-pre class="language-html"><span class="hljs-tag">&lt;<span class="hljs-name">template</span>&gt;</span>
  <span class="hljs-tag">&lt;<span class="hljs-name">section</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">btn</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;primary&quot;</span> @<span class="hljs-attr">click</span>=<span class="hljs-string">&quot;show = !show&quot;</span>&gt;</span>Toggle Collapsing Alert<span class="hljs-tag">&lt;/<span class="hljs-name">btn</span>&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">hr</span>/&gt;</span>
    <span class="hljs-tag">&lt;<span class="hljs-name">collapse</span> <span class="hljs-attr">v-model</span>=<span class="hljs-string">&quot;show&quot;</span>&gt;</span>
      <span class="hljs-tag">&lt;<span class="hljs-name">alert</span> <span class="hljs-attr">type</span>=<span class="hljs-string">&quot;warning&quot;</span> <span class="hljs-attr">dismissible</span> @<span class="hljs-attr">dismissed</span>=<span class="hljs-string">&quot;show = false&quot;</span>&gt;</span>This alert <span class="hljs-tag">&lt;<span class="hljs-name">b</span>&gt;</span>will collapse on open / close<span class="hljs-tag">&lt;/<span class="hljs-name">b</span>&gt;</span>.<span class="hljs-tag">&lt;/<span class="hljs-name">alert</span>&gt;</span>
    <span class="hljs-tag">&lt;/<span class="hljs-name">collapse</span>&gt;</span>
  <span class="hljs-tag">&lt;/<span class="hljs-name">section</span>&gt;</span>
<span class="hljs-tag">&lt;/<span class="hljs-name">template</span>&gt;</span>
<span class="hljs-tag">&lt;<span class="hljs-name">script</span>&gt;</span><span class="language-javascript">
  <span class="hljs-keyword">export</span> <span class="hljs-keyword">default</span> {
    <span class="hljs-title function_">data</span> () {
      <span class="hljs-keyword">return</span> {
        <span class="hljs-attr">show</span>: <span class="hljs-literal">true</span>
      }
    }
  }
</span><span class="hljs-tag">&lt;/<span class="hljs-name">script</span>&gt;</span>
<span class="hljs-comment">&lt;!-- alert-with-collapse.vue --&gt;</span>
</code></pre>
<h1 id="api-reference">API Reference <a class="header-anchor" href="#api-reference">&#128279;</a></h1>
<h2 id="alert-1"><a href="https://github.com/wxsms/uiv/blob/0.x/src/components/alert/Alert.vue">Alert</a> <a class="header-anchor" href="#alert-1">&#128279;</a></h2>
<h3 id="props">Props <a class="header-anchor" href="#props">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Type</th>
<th>Default</th>
<th>Required</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>dismissible</code></td>
<td>Boolean</td>
<td>false</td>
<td></td>
<td>Show dismiss button in alert.</td>
</tr>
<tr>
<td><code v-pre>type</code></td>
<td>String</td>
<td>info</td>
<td></td>
<td>Alert type (success, info, warning, danger).</td>
</tr>
<tr>
<td><code v-pre>duration</code></td>
<td>Number</td>
<td>0</td>
<td></td>
<td>Dismiss after milliseconds, use 0 to prevent self-closing.</td>
</tr>
</tbody>
</table></div><h3 id="slots">Slots <a class="header-anchor" href="#slots">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>default</code></td>
<td>The alert body.</td>
</tr>
</tbody>
</table></div><h3 id="events">Events <a class="header-anchor" href="#events">&#128279;</a></h3>
<div class="table-responsive"><table class="table table-bordered table-hover"><thead>
<tr>
<th>Name</th>
<th>Params</th>
<th>Description</th>
</tr>
</thead>
<tbody>
<tr>
<td><code v-pre>dismissed</code></td>
<td></td>
<td>Fire after the alert dismissed. Note: you have to hide / destroy the alert using <code v-pre>v-if</code> / <code v-pre>v-show</code> / <code v-pre>v-for</code> manually due to child components can't change state of parent component.</td>
</tr>
</tbody>
</table></div></markdown-wrapper></template><script>
  

  

  
export default {components:{'alert-examples':{"template":"<div class=\"markdown-live-example\"><alert type=\"success\"><b>Well done!</b> You successfully read this important alert message.</alert>\n<alert type=\"info\"><b>Heads up!</b> This alert needs your attention, but it's not super important.</alert>\n<alert type=\"warning\"><b>Warning!</b> Better check yourself, you're not looking too good.</alert>\n<alert type=\"danger\"><b>Oh snap!</b> Change this and that and try again.</alert>\n<!-- alert-examples.vue --></div>"},'alert-dismissible':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <alert type=\"warning\" v-if=\"show\" dismissible @dismissed=\"show = false\">\n      <b>Warning!</b> Better check yourself, you're not looking too good.\n    </alert>\n    <alert v-for=\"(item, index) in alerts\" dismissible :key=\"item.key\" @dismissed=\"alerts.splice(index, 1)\">\n      <b>Heads up!</b> This alert needs your attention, but it's not super important.\n    </alert>\n    <hr/>\n    <btn type=\"primary\" @click=\"addDismissibleAlert()\">Add Dismissible Alert</btn>\n  </section>\n</div>",
    data () {
      return {
        show: true,
        alerts: []
      }
    },
    methods: {
      addDismissibleAlert () {
        this.alerts.push({key: new Date().getTime()})
      }
    }
  },'alert-auto-dismissing':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <alert v-for=\"(item, index) in alerts\" :duration=\"duration\" :key=\"item.key\" @dismissed=\"alerts.splice(index, 1)\">\n      This alert <b>will dismiss after {{duration}}ms</b>.\n    </alert>\n    <hr/>\n    <btn type=\"primary\" @click=\"addAutoDismissAlert()\">Add Auto Dismiss Alert</btn>\n  </section>\n</div>",
    data () {
      return {
        alerts: [],
        duration: 2000
      }
    },
    methods: {
      addAutoDismissAlert () {
        this.alerts.push({key: new Date().getTime()})
      }
    }
  },'alert-with-collapse':{"template":"<div class=\"markdown-live-example\">\n  <section>\n    <btn type=\"primary\" @click=\"show = !show\">Toggle Collapsing Alert</btn>\n    <hr/>\n    <collapse v-model=\"show\">\n      <alert type=\"warning\" dismissible @dismissed=\"show = false\">This alert <b>will collapse on open / close</b>.</alert>\n    </collapse>\n  </section>\n</div>",
    data () {
      return {
        show: true
      }
    }
  }}}</script>